import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { templatesApi } from '../services/api';
import { Template } from '../types';
import { useAuth } from '../context/AuthContext';
import {
  Plus,
  FolderOpen,
  Eye,
  Copy,
  Trash2,
  AlertCircle,
  FileText,
  CheckCircle,
  XCircle,
  Download,
  Upload,
  Search
} from 'lucide-react';

export default function TemplatesList() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { isAdmin } = useAuth();
  const [search,setSearch]=useState('');
  const [category,setCategory]=useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const { templates } = await templatesApi.getAll();
      setTemplates(templates);
    } catch (err) {
      setError('Fehler beim Laden der Vorlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicate = async (id: string, name: string) => {
    try {
      const newName = window.prompt('Name für die neue Vorlage:', `${name} (Kopie)`);
      if (!newName) return;
      
      await templatesApi.duplicate(id, newName);
      await loadTemplates();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Duplizieren fehlgeschlagen');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    
    try {
      await templatesApi.delete(id);
      setTemplates(templates.filter(t => t.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };
  const handleExport=async(id:string,name:string)=>{const data=await templatesApi.exportPackage(id);const url=URL.createObjectURL(new Blob([JSON.stringify(data,null,2)],{type:'application/json'}));const link=document.createElement('a');link.href=url;link.download=`${name.replace(/[^a-z0-9-_]/gi,'-')}.contwave.json`;link.click();URL.revokeObjectURL(url)};
  const handleImport=async(file?:File)=>{if(!file)return;try{await templatesApi.importPackage(JSON.parse(await file.text()));await loadTemplates()}catch(err:any){setError(err.response?.data?.error||'Vorlagenimport fehlgeschlagen')}};
  const visible=templates.filter(template=>(!search||`${template.name} ${template.description||''}`.toLowerCase().includes(search.toLowerCase()))&&(!category||template.category===category));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vorlagen</h1>
          <p className="mt-1 text-sm text-gray-500">
            {templates.length} Vorlage(n) verfügbar
          </p>
        </div>
        {isAdmin && (
          <Link
            to="/admin/templates/new"
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neue Vorlage
          </Link>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}
      <div className="mb-6 bg-white border rounded-lg p-3 flex flex-wrap gap-3"><label className="relative flex-1 min-w-56"><Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"/><input aria-label="Vorlagen suchen" className="w-full border rounded-lg py-2 pl-9 pr-3" placeholder="Name oder Beschreibung suchen …" value={search} onChange={e=>setSearch(e.target.value)}/></label><select aria-label="Kategorie filtern" className="border rounded-lg px-3" value={category} onChange={e=>setCategory(e.target.value)}><option value="">Alle Kategorien</option>{Array.from(new Set(templates.map(t=>t.category).filter((value):value is string=>Boolean(value)))).map(value=><option key={value} value={value}>{value}</option>)}</select>{isAdmin&&<label className="inline-flex items-center gap-2 border rounded-lg px-3 cursor-pointer"><Upload className="h-4 w-4"/>Vorlage importieren<input className="sr-only" type="file" accept="application/json,.json" onChange={e=>handleImport(e.target.files?.[0])}/></label>}</div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Vorlagen vorhanden</h3>
          <p className="text-gray-500 mb-4">
            {isAdmin ? 'Erstellen Sie Ihre erste Vorlage' : 'Es wurden noch keine Vorlagen angelegt'}
          </p>
          {isAdmin && (
            <Link
              to="/admin/templates/new"
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Vorlage erstellen
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <div className="p-2 bg-primary-50 rounded-lg mr-3">
                      <FileText className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{template.name}</h3>
                      <p className="text-xs text-gray-500">
                        {template.category||'Ohne Kategorie'} · Revision {template.revision||1}
                      </p>
                    </div>
                  </div>
                  <span className={`flex items-center text-xs font-medium ${
                    template.isActive ? 'text-green-600' : 'text-gray-400'
                  }`}>
                    {template.isActive ? (
                      <><CheckCircle className="h-3 w-3 mr-1" /> Aktiv</>
                    ) : (
                      <><XCircle className="h-3 w-3 mr-1" /> Inaktiv</>
                    )}
                  </span>
                </div>
                
                {template.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {template.description}
                  </p>
                )}

                <div className="text-xs text-gray-500 mb-4">
                  Erstellt von {template.createdBy?.firstName} {template.createdBy?.lastName}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <Link
                    to={`/templates/${template.id}`}
                    className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    {isAdmin ? 'Bearbeiten' : 'Ansehen'}
                  </Link>
                  <div className="flex items-center space-x-2">
                    <button onClick={()=>handleExport(template.id,template.name)} className="p-1.5 text-gray-400 hover:text-primary-600 rounded" title="Exportieren"><Download className="h-4 w-4"/></button>
                    <button
                      onClick={() => handleDuplicate(template.id, template.name)}
                      className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                      title="Duplizieren"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
